"use client";

import { motion } from "framer-motion";
import { GripHorizontal, RefreshCcw, X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { Music } from "@/components/music";
import { cn } from "@/lib/utils";

type CSSVarStyle = React.CSSProperties & Record<`--${string}`, string | number>;

const MusicIcon = ({
  className,
  isActive,
  onActivate,
}: {
  className?: string;
  isActive?: boolean;
  onActivate?: () => void;
}) => {
  const bars = 7;
  const baseProfiles = [
    { base: 0.18, peak: 0.46, duration: 0.9 },
    { base: 0.2, peak: 0.5, duration: 1.05 },
    { base: 0.16, peak: 0.44, duration: 0.98 },
    { base: 0.22, peak: 0.52, duration: 1.2 },
    { base: 0.18, peak: 0.48, duration: 1.05 },
  ];

  const barConfigs = useMemo(
    () =>
      Array.from({ length: bars }, (_, index) => {
        const profile = baseProfiles[index % baseProfiles.length];
        const jitter = 0.9 + (index % 3) * 0.04;
        return {
          base: profile.base * (0.92 + (index % 2) * 0.05),
          peak: profile.peak * (0.94 + ((index + 1) % 2) * 0.04),
          duration: profile.duration * jitter,
          delay: (index % 4) * 0.12,
          direction: index % 2 === 0 ? "alternate" : "alternate-reverse",
        };
      }),
    [bars],
  );
  const isInteractive =
    typeof isActive === "undefined" || typeof onActivate === "function";
  const handleToggle = useCallback(() => {
    if (!isInteractive) return;
    onActivate?.();
  }, [isInteractive, onActivate]);
  const isPlaying = isActive ?? false;

  return (
    <span
      onClick={handleToggle}
      aria-label={
        isInteractive
          ? isPlaying
            ? "Pause waveform"
            : "Play waveform"
          : undefined
      }
      aria-pressed={isInteractive ? isActive ?? false : undefined}
      tabIndex={isInteractive ? 0 : -1}
      className={cn(
        "flex size-full items-center justify-center gap-[2px] overflow-hidden",
        isInteractive ? "cursor-pointer" : "cursor-default",
        className,
      )}
      role={isInteractive ? "button" : undefined}
    >
      {barConfigs.map((config, index) => {
        const style: CSSVarStyle = {
          "--bar-base": Number(config.base.toFixed(3)),
          "--bar-peak": Number(config.peak.toFixed(3)),
          transform: `scaleY(${config.base.toFixed(3)}) translateZ(0)`,
          opacity: isPlaying ? 1 : 0.7,
          willChange: "transform",
        };

        if (isPlaying) {
          style.animationName = "island-bar-pulse";
          style.animationDuration = `${config.duration.toFixed(2)}s`;
          style.animationDelay = `${config.delay.toFixed(2)}s`;
          style.animationTimingFunction = "ease-in-out";
          style.animationIterationCount = "infinite";
          style.animationDirection = config.direction;
        } else {
          style.animationName = "none";
        }

        return (
          <span
            key={index}
            className="island-bar h-full w-[1.5px] rounded-full bg-white dark:bg-black motion-reduce:animate-none"
            style={style}
            aria-hidden="true"
          />
        );
      })}
    </span>
  );
};

const DynamicIsland = ({
  view,
  variantKey,
  isMusicActive,
  onIdleActivate,
  onMusicDeactivate,
  musicContent,
}: {
  view: string;
  variantKey: string;
  isMusicActive?: boolean;
  onIdleActivate?: () => void;
  onMusicDeactivate?: () => void;
  musicContent?: ReactNode;
}) => {
  const content = useMemo(() => {
    switch (view) {
      case "music":
        return (
          <div className="relative">
            {onMusicDeactivate ? (
              <button
                type="button"
                onClick={onMusicDeactivate}
                className="text-white/70 dark:text-black/70 hover:text-white/90 focus-visible:ring-white/50 absolute right-3 top-3 flex size-6 items-center justify-center rounded-full transition focus-visible:outline-none focus-visible:ring-2"
              >
                <span className="sr-only">Collapse music</span>
                <X className="size-4" aria-hidden="true" />
              </button>
            ) : null}
            {/* {musicContent ?? <Music />} */}
            {musicContent}
          </div>
        );
      case "idle":
        return (
          <div
            role={onIdleActivate ? "button" : undefined}
            tabIndex={onIdleActivate ? 0 : -1}
            onClick={onIdleActivate}
            onKeyDown={(event) => {
              if (!onIdleActivate) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onIdleActivate();
              }
            }}
            className={cn(
              "flex h-9 w-[68px] items-center justify-center px-2 outline-none",
              onIdleActivate ? "cursor-pointer focus-visible:ring-2 focus-visible:ring-white/40" : "cursor-default",
            )}
          >
            <MusicIcon
              className="h-7 w-full"
              isActive={isMusicActive}
              onActivate={onIdleActivate}
            />
          </div>
        );
    }
  }, [view, isMusicActive, onIdleActivate, onMusicDeactivate, musicContent]);

  const verticalShift =
    VIEW_VERTICAL_SHIFTS[view as keyof typeof VIEW_VERTICAL_SHIFTS] ?? 0;

  return (
    <motion.div
      layout
      animate={{ y: verticalShift }}
      transition={{
        type: "spring",
        bounce:
          BOUNCE_VARIANTS[variantKey as keyof typeof BOUNCE_VARIANTS] || 0.2,
      }}
      style={{ borderRadius: 24, transformOrigin: "50% 100%"  }}
      className={cn(
        "mx-auto flex w-fit min-w-[60px] items-start justify-center rounded-full bg-black dark:bg-white/80",
        view === "music" && musicContent ? "overflow-visible" : "overflow-hidden",
      )}
    >
      <motion.div
        transition={{
          type: "spring",
          bounce:
            BOUNCE_VARIANTS[variantKey as keyof typeof BOUNCE_VARIANTS] || 0.2,
        }}
        initial={{
          scale: 0.9,
          opacity: 0,
          filter: "blur(5px)",
          originX: 0.5,
          originY: 1,
        }}
        animate={{
          scale: 1,
          opacity: 1,
          filter: "blur(0px)",
          originX: 0.5,
          originY: 1,
          transition: {
            delay: 0.05,
          },
        }}
        key={view}
      >
        {content}
      </motion.div>
    </motion.div>
  );
};

const Options = ({
  view,
  setView,
  setVariantKey,
}: {
  view: string;
  setView: (v: string) => void;
  setVariantKey: (v: string) => void;
}) => {
  return (
    <motion.div
      drag
      className="top-30 border-foreground/10 bg-muted2 absolute right-1/2 flex w-[245px] translate-x-1/2 flex-col gap-3 rounded-3xl border p-3 backdrop-blur-sm lg:right-4 lg:translate-x-0"
    >
      <div className="flex items-center justify-between">
        <span className="size-4 cursor-grab active:cursor-grabbing">
          <GripHorizontal className="size-4 opacity-50" />
        </span>
        <p
          onClick={() => {
            setView("idle");
            setVariantKey("idle");
          }}
          className="hover:bg-muted3 group flex cursor-pointer items-center justify-center gap-2 rounded-lg px-2 py-1 text-sm opacity-50"
        >
          Options
          <span className="group-active:-rotate-360 rotate-0 cursor-pointer transition-all duration-300 group-hover:rotate-90">
            <RefreshCcw className="size-4 opacity-50" />
          </span>{" "}
        </p>
      </div>
      <div className="flex flex-wrap justify-center gap-2 pt-2">
        {[
          "idle",
          "music",
        ].map((v) => (
          <button
            type="button"
            className="bg-muted3 text-foreground flex-1 rounded-xl px-2.5 py-1.5 text-xs"
            onClick={() => {
              if (v === "idle") {
                setView("idle");
                setVariantKey("idle");
                return;
              }
              setView("idle");
              setTimeout(() => setView(v), 500);
              setVariantKey(`${view}-${v}`);
            }}
            key={v}
          >
            {v}
          </button>
        ))}
      </div>
    </motion.div>
  );
};

const Skiper2 = () => {
  const [view, setView] = useState("idle");
  const [variantKey, setVariantKey] = useState("idle");

  const openMusic = () => {
    setVariantKey("idle-music");
    setView("music");
  };

  const closeMusic = () => {
    setVariantKey("music-idle");
    setView("idle");
  };

  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center">
      <DynamicIsland
        view={view}
        variantKey={variantKey}
        onIdleActivate={openMusic}
        onMusicDeactivate={closeMusic}
      />
      <Options view={view} setView={setView} setVariantKey={setVariantKey} />
    </div>
  );
};

const BOUNCE_VARIANTS = {
  idle: 0.5,
  "idle-music": 0.2,
  "ring-idle": 0.5,
  "timer-ring": 0.35,
  "ring-timer": 0.35,
  "timer-idle": 0.3,
  "idle-timer": 0.3,
  "idle-ring": 0.5,
  "music-timer": 0.2,
  "music-ring": 0.2,
  "music-idle": 0.3,
  "aridrop-ring": 0.2,
  "aridrop-timer": 0.2,
  "aridrop-record": 0.2,
  "music-record": 0.2,
  "music-airdrop": 0.2,
  "airdrop-airdropMini": 0.2,
  "airdropMini-airdrop": 0.2,
};

const VIEW_VERTICAL_SHIFTS = {
  music: 0,
} as const;

export { Skiper2, DynamicIsland, Options };
